import jsQR from 'jsqr'
import { useEffect, useRef, useState } from 'react'

type Props = {
  onScan: (raw: string) => void
  active?: boolean
}

/** Camera QR reader — BarcodeDetector when available, jsQR otherwise. */
export function QrScanner({ onScan, active = true }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const onScanRef = useRef(onScan)
  const handled = useRef(false)
  const [error, setError] = useState<string | null>(null)

  onScanRef.current = onScan

  useEffect(() => {
    if (!active) return

    handled.current = false
    let stream: MediaStream | null = null
    let raf = 0
    let cancelled = false
    if (!videoRef.current || !canvasRef.current) return
    const videoEl: HTMLVideoElement = videoRef.current
    const canvasEl: HTMLCanvasElement = canvasRef.current

    const detector =
      typeof BarcodeDetector !== 'undefined'
        ? new BarcodeDetector({ formats: ['qr_code'] })
        : null

    async function tick() {
      if (cancelled || handled.current) return
      if (videoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        const w = videoEl.videoWidth
        const h = videoEl.videoHeight
        if (w && h) {
          canvasEl.width = w
          canvasEl.height = h
          const ctx = canvasEl.getContext('2d', { willReadFrequently: true })
          if (ctx) {
            ctx.drawImage(videoEl, 0, 0, w, h)
            try {
              if (detector) {
                const codes = await detector.detect(canvasEl)
                const raw = codes[0]?.rawValue
                if (raw) {
                  handled.current = true
                  onScanRef.current(raw)
                  return
                }
              } else {
                const image = ctx.getImageData(0, 0, w, h)
                const code = jsQR(image.data, w, h, {
                  inversionAttempts: 'dontInvert',
                })
                if (code?.data) {
                  handled.current = true
                  onScanRef.current(code.data)
                  return
                }
              }
            } catch {
              /* keep scanning */
            }
          }
        }
      }
      raf = requestAnimationFrame(() => void tick())
    }

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        })
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop()
          return
        }
        videoEl.srcObject = stream
        await videoEl.play()
        raf = requestAnimationFrame(() => void tick())
      } catch {
        if (!cancelled) {
          setError(
            'Camera niet beschikbaar. Open de uitnodigingslink of geef cameratoegang.'
          )
        }
      }
    })()

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      if (stream) {
        for (const track of stream.getTracks()) track.stop()
      }
      videoEl.srcObject = null
    }
  }, [active])

  return (
    <div className="w-full">
      <div className="relative overflow-hidden bg-black aspect-square">
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          playsInline
          muted
        />
        <canvas ref={canvasRef} className="hidden" />
        <div className="pointer-events-none absolute inset-8 rounded-xl border-2 border-white/50" />
      </div>
      {error ? (
        <p className="px-5 py-3 text-sm text-red-300">{error}</p>
      ) : (
        <p className="px-5 py-3 text-sm opacity-70">
          Richt de camera op de QR-code van de uitnodiging.
        </p>
      )}
    </div>
  )
}

declare class BarcodeDetector {
  constructor(options?: { formats?: string[] })
  detect(source: ImageBitmapSource): Promise<Array<{ rawValue: string }>>
}
